import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "@helpers/translate";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import { getPairMeta } from "../Quotes/pairMeta";
import {
  BADGE_ICONS,
  COIN_ICONS,
} from "../Orders/Community/communityMockData";
import { CustomScrollTable } from "../CustomScrollTable";
import { LONG_COLOR, SHORT_COLOR } from "../Controls/TradeForm/constants";
import { TradeModal } from "./TradeModal";
import { DetailRow } from "./components/DetailRow";
import { ModalCloseButton } from "./components/ModalCloseButton";
import "./TraderProfileModal.sass";

const TAB_TRADES = "trades";
const TAB_STATS = "stats";
const TAB_ACHIEVEMENTS = "achievements";
const LIST_MAX_HEIGHT = 300;

const Row = (props) => <DetailRow block="trader-profile" {...props} />;

/**
 * Photo, or the first letter of the name when there is none or it fails to load.
 */
const Avatar = ({ src, name }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => setHasError(false), [src]);

  return src && !hasError ? (
    <img
      src={src}
      alt=""
      className="trader-profile__avatar"
      onError={() => setHasError(true)}
    />
  ) : (
    <span className="trader-profile__avatar trader-profile__avatar--letter">
      {String(name ?? "?").charAt(0).toUpperCase()}
    </span>
  );
};

/**
 * Open trade of the trader, laid out as the mobile trade cards.
 */
const TradeRow = ({ trade, onClick }) => {
  const coin = trade.trade?.coin ?? "";
  const coinIcon = COIN_ICONS[trade.trade?.coinIcon] ?? COIN_ICONS[coin];
  const isDown = trade.trade?.direction === "down";
  const tone = String(trade.profitLoss).startsWith("-") ? "down" : "up";

  return (
    <button type="button" className="trader-profile__trade" onClick={onClick}>
      <span className="trader-profile__trade-coin">
        {coinIcon && (
          <img src={coinIcon} alt="" className="trader-profile__trade-icon" />
        )}
        <span className="trader-profile__trade-badge">
          {isDown ? (
            <TradeArrowDownIcon size={15} color={SHORT_COLOR} />
          ) : (
            <TradeArrowUpIcon size={15} color={LONG_COLOR} />
          )}
        </span>
      </span>

      <span className="trader-profile__trade-info">
        <span>
          <span className="trader-profile__trade-ticker">{coin}</span>{" "}
          <span className="trader-profile__muted">@</span> {trade.entry}
        </span>
        <span>{trade.price}</span>
        <span>
          {trade.wager}{" "}
          <span className="trader-profile__tone--up">{trade.leverage}</span>
        </span>
      </span>

      <span className="trader-profile__trade-result">
        <span className={`trader-profile__tone--${tone}`}>{trade.multiplier}</span>
        <span className={`trader-profile__tone--${tone}`}>{trade.profitLoss}</span>
        <span>{trade.value}</span>
      </span>
    </button>
  );
};

/**
 * Trader profile from the leaderboard (docs/trade-logic.md 2.7 ?modal=user).
 *
 * @param {object} props
 * @param {object|null} props.trader result of communityService.getTraderProfile,
 *   the modal is shown while it's set
 * @param {() => void} props.onClose
 * @param {(trade: object) => void} [props.onTradeClick] an open trade of the Trades tab
 */
export const TraderProfileModal = ({ trader, onClose, onTradeClick }) => {
  const { __ } = useTranslation();
  const [tab, setTab] = useState(TAB_STATS);
  const username = trader?.user;

  useEffect(() => {
    setTab(TAB_STATS);
  }, [username]);

  if (!trader) return null;

  const favoriteSymbol = trader.favoriteAsset?.symbol;
  const favoriteIcon =
    COIN_ICONS[trader.favoriteAsset?.assetIcon] ?? COIN_ICONS[favoriteSymbol];
  const favoriteName =
    getPairMeta(`${favoriteSymbol}USDT`)?.fullName ?? favoriteSymbol;
  const closedTrades = parseInt(trader.trades, 10);
  const tabs = [
    [TAB_TRADES, __("futures.trader_profile.trades")],
    [TAB_STATS, __("futures.trader_profile.stats")],
    [TAB_ACHIEVEMENTS, __("futures.trader_profile.achievements")],
  ];

  return (
    <TradeModal
      isOpen
      onClose={onClose}
      className="trader-profile"
      labelledBy="trader-profile-title"
    >
      <ModalCloseButton
        className="trader-profile__close"
        onClick={onClose}
        iconSize={20}
      />

      <Avatar src={trader.avatar} name={trader.user} />
      <h3 id="trader-profile-title" className="trader-profile__name">
        {trader.user}
      </h3>
      <p className="trader-profile__followers">
        {trader.followers ?? "—"} {__("futures.trader_profile.followers")}
      </p>

      <div className="trader-profile__tabs" role="tablist">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={classnames("trader-profile__tab", {
              "trader-profile__tab--active": tab === id,
            })}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === TAB_STATS && (
        <>
          <div className="trader-profile__stats">
            <Row label={__("futures.trader_profile.win_rate")}>
              {trader.winRate ?? "—"}
            </Row>
            <Row label={__("futures.trader_profile.biggest_profit")}>
              {trader.biggestMultiplier ?? trader.biggestWin ?? "—"}
            </Row>
            <Row label={__("futures.trader_profile.favorite_asset")}>
              {favoriteIcon && (
                <img
                  src={favoriteIcon}
                  alt=""
                  className="trader-profile__asset-icon"
                />
              )}
              {favoriteName ?? "—"}
            </Row>
            <Row label={__("futures.trader_profile.total_wagered")}>
              {trader.totalWagered ?? "—"}
            </Row>
            <Row label={__("futures.trader_profile.total_closed_trades")}>
              {Number.isFinite(closedTrades) ? closedTrades : "—"}
            </Row>
          </div>

          <div className="trader-profile__member-since">
            <Row label={__("futures.trader_profile.member_since")}>
              {trader.memberSince ?? "—"}
            </Row>
          </div>
        </>
      )}

      {tab === TAB_TRADES &&
        (trader.openTrades?.length ? (
          <CustomScrollTable
            isTable={false}
            maxHeight={LIST_MAX_HEIGHT}
            containerClassName="trader-profile__scroll"
          >
            {trader.openTrades.map((trade, index) => (
              <TradeRow
                key={trade.tradeId ?? index}
                trade={trade}
                onClick={() => onTradeClick?.(trade)}
              />
            ))}
          </CustomScrollTable>
        ) : (
          <p className="trader-profile__empty">
            {__("futures.trader_profile.no_trades")}
          </p>
        ))}

      {tab === TAB_ACHIEVEMENTS &&
        (trader.achievements?.length ? (
          <CustomScrollTable
            isTable={false}
            maxHeight={LIST_MAX_HEIGHT}
            containerClassName="trader-profile__scroll"
          >
            <div className="trader-profile__achievements">
              {trader.achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="trader-profile__achievement"
                >
                  <img
                    src={BADGE_ICONS[achievement.badgeIcon]}
                    alt=""
                    className="trader-profile__achievement-icon"
                  />
                  <span className="trader-profile__achievement-title">
                    {achievement.title}
                  </span>
                  <span className="trader-profile__achievement-date">
                    {achievement.date}
                  </span>
                </div>
              ))}
            </div>
          </CustomScrollTable>
        ) : (
          <p className="trader-profile__empty">
            {__("futures.trader_profile.no_achievements")}
          </p>
        ))}

      {/* Disabled: there is no follow API */}
      <button type="button" className="trader-profile__submit" disabled>
        {__("futures.trader_profile.follow")}
      </button>
    </TradeModal>
  );
};
